import { ReactElement, useCallback, useEffect } from 'react';
import Button from '@components/Button';
import StackView from '@components/StackView';
import DepositInfo from './DepositInfo';
import TransferInfo from './TransferInfo';
import TransactionInfoContainer from './TransactionInfoContainer';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import { Network, TxStatus, TxStep } from '@types';
import useSendTransactionContext from '@hooks/useSendTransactionContext';
import { getDisplaySymbol, isBetaTx } from '@lib/utils';
import * as ga from '@lib/ga';

function BridgeTransactionView(): ReactElement {
  const { onReset } = useTransferAssetFlowContext();
  const { bridgeTxNotifications, showBridgeTxNotification } =
    useSendTransactionContext();
  const latestBridgeTx =
    bridgeTxNotifications[bridgeTxNotifications.length - 1].bridgeTx;
  const { depositTx, transferTx } = latestBridgeTx;

  const shouldMinimizeWindow =
    transferTx.status === TxStatus.pending ||
    (transferTx.status === TxStatus.fulfilled && !transferTx.hash);
  const hasBridgeTransactionFailed =
    depositTx.status === TxStatus.failed ||
    transferTx.status === TxStatus.failed;

  const betaTx = isBetaTx(latestBridgeTx);

  const handleCloseButtonClick = useCallback(() => {
    onReset();
    if (shouldMinimizeWindow && !hasBridgeTransactionFailed && !betaTx)
      showBridgeTxNotification(latestBridgeTx.id);
  }, [
    onReset,
    shouldMinimizeWindow,
    hasBridgeTransactionFailed,
    showBridgeTxNotification,
    latestBridgeTx.id,
    betaTx,
  ]);

  useEffect(() => {
    if (depositTx?.hash) {
      ga.event({
        action: 'txn',
        params: {
          hitType: 'event',
          eventCategory: 'Transaction',
          eventAction: 'send',
          eventLabel: `source-tx-${depositTx.hash}`,
        },
      });
    }
  }, [depositTx.hash]);

  const displayFailedOrNotFailedContent = () => {
    return (
      <StackView direction="vertical" spacing={3}>
        <TransactionInfoContainer
          txnStep={TxStep.deposit}
          txnStatus={betaTx ? TxStatus.fulfilled : depositTx.status}
          loading={betaTx ? false : depositTx.status === TxStatus.pending}
          txnErrorMessage={
            depositTx.status === TxStatus.failed
              ? 'Transaction failed and reverted. Make sure you are paying enough gas fees and try again!'
              : ''
          }
        >
          <DepositInfo
            amount={depositTx.amount}
            // TODO: add test for this after instegrating with txs api
            currencySymbol={getDisplaySymbol(depositTx.asset?.symbol || '')}
            network={depositTx.network}
            txnHash={depositTx.hash || ''}
            chain={depositTx.chain}
            betaTx={betaTx}
          />
        </TransactionInfoContainer>

        <TransactionInfoContainer
          txnStep={TxStep.transfer}
          txnStatus={betaTx ? TxStatus.fulfilled : transferTx.status}
          loading={
            depositTx.status === TxStatus.fulfilled &&
            transferTx.status === TxStatus.pending &&
            !!transferTx.hash
          }
          txnErrorMessage={
            transferTx.status === TxStatus.failed && !!transferTx.hash
              ? 'Transaction failed and reverted on the blockchain. Please check your transaction settings and retry.'
              : ''
          }
        >
          <TransferInfo
            txnStatus={transferTx.status}
            txnHash={transferTx.hash || ''}
            network={transferTx.network}
            chain={transferTx.chain}
            betaTx={betaTx}
          />
        </TransactionInfoContainer>
      </StackView>
    );
  };

  return (
    <StackView direction="vertical" spacing={3}>
      {displayFailedOrNotFailedContent()}
      <div className="tablet:w-4/5 mobile:w-full mx-auto">
        <Button className="w-full" onClick={handleCloseButtonClick}>
          {shouldMinimizeWindow && !hasBridgeTransactionFailed && !betaTx
            ? 'Minimize Window'
            : 'Close'}
        </Button>
      </div>
    </StackView>
  );
}

export default BridgeTransactionView;
